"use client";

import HeaderPortal from "components/portal/header-portal";
import { Header } from "@/components/header";
import Loader from "@/components/loader";
import ErrorState from "@/components/states/error";
import { StatusDisplay } from "@/components/status/display";
import { useFetch } from "@/hooks/useFetch";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/components/dialog";
import { formatDateForDisplay, invalidateAllQueries } from "utils/utils";
import { formatName } from "utils/index";
import AvatarDisplay from "components/avatar/display";
import { fetchTaskDetails, updateTask, fetchJobExtensionDetails } from "./api";
import TaskAttachments from "./components/attachments";
import TaskDescription from "./components/description";

export default function Task() {
  const { taskId } = useParams();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const {
    data: task,
    isLoading,
    isError,
  } = useFetch({
    queryKey: ["task", taskId],
    queryFn: () => fetchTaskDetails(taskId),
  });

  const collection = task?.collection?.[0]?.collection;
  const item = task?.collection?.[0]?.item;
  const isJobExtension = collection === "job_extensions";
  const extensionId = item?.id;

  const {
    data: extension,
    isLoading: isExtensionLoading,
  } = useFetch({
    queryKey: ["job_extension_job_id", extensionId],
    queryFn: () => fetchJobExtensionDetails(extensionId),
    enabled: !!(isJobExtension && extensionId),
  });

  const handleUpdateTask = async (item) => {
    try {
      setSubmitting(true);
      const res = await updateTask(taskId, item);
      toast.success("Task updated successfully");
      invalidateAllQueries(["tasks", taskId]);
      setConfirmOpen(false);
      setSubmitting(false);
    } catch (error) {
      toast.error(error.message);
      console.error(error);
      setSubmitting(false);
    }
  };

  if (isLoading || (isJobExtension && isExtensionLoading)) {
    return (
      <div className="flex-1">
        <Loader />
      </div>
    );
  }

  if (isError || !task) {
    return (
      <div className="flex-1">
        <ErrorState message="Task not found." />
      </div>
    );
  }

  const getRedirectionData = () => {
    switch (collection) {
      case "jobs":
        return { href: `/work/jobs/${item?.id}`, label: "Job" };
      case "job_extensions": {
        const jobId = extension?.job?.id;
        return { href: `/work/jobs/${jobId}/extensions/${item?.id}`, label: "Extension" };
      }
      case "projects":
        return { href: `/work/projects/${item?.project_id}`, label: "Project" };
      case "billables":
        return { href: `/work/billables/${item?.id}`, label: "Billable" };
      case "daily_work_capacity":
        return {
          href: `/timesheet/logs?date=${item?.date}`,
          label: "Timesheet",
        };
      default:
        return { href: "#", label: "Item" };
    }
  };

  const { href, label } = getRedirectionData();

  return (
    <>
      <div className="p-6 flex-1 space-y-6">
        {/* Header Section */}
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-2">
              <Header.Title title={task.title} />
              <StatusDisplay value={task.status} />
            </div>
          </div>
        </div>

        {/* Date Information Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex flex-col gap-3 border border-gray-200 bg-white p-5 rounded-lg">
            <div className="text-sm font-medium text-gray-500 tracking-wide">
              Due Date
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {formatDateForDisplay(task.due_date, { addSuffix: true })}
            </div>
          </div>

          <div className="flex flex-col gap-3 border border-gray-200 bg-white p-5 rounded-lg">
            <div className="text-sm font-medium text-gray-500 tracking-wide">
              Created Date
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {formatDateForDisplay(task.date_created, { addSuffix: true })}
            </div>
          </div>

          <div className="flex flex-col gap-3 border border-gray-200 bg-white p-5 rounded-lg">
            <div className="text-sm font-medium text-gray-500 tracking-wide">
              Assigned By
            </div>
            <div className="flex items-center gap-2">
              {task.user_created ? (
                <>
                  <AvatarDisplay className="w-8 h-8" user={task.user_created} />
                  <span className="text-xl font-semibold text-gray-900">
                    {formatName(task.user_created)}
                  </span>
                </>
              ) : (
                <span className="text-xl font-semibold text-gray-400">
                  Unknown
                </span>
              )}
            </div>
          </div>
        </div>

        <TaskDescription description={task.description} />

        <TaskAttachments attachments={task.attachments} />
      </div>

      {(task.status == "open" ||
        task.status == "in_progress" ||
        task.status == "in-progress") && (
        <HeaderPortal>
          <div className="flex justify-end">
            <Button onClick={() => setConfirmOpen(true)}>
              Mark as Completed
            </Button>
          </div>
        </HeaderPortal>
      )}
      {collection && (
        <HeaderPortal>
          <div className="flex justify-end">
            <Button asChild>
              <Link to={href}>View {label}</Link>
            </Button>
          </div>
        </HeaderPortal>
      )}


      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark this task as completed?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              disabled={submitting}
              onClick={() => handleUpdateTask({ status: "completed" })}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
